import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import * as orderService from '../../services/orderService'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonListPage } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/ui/EmptyState'
import { LoadMoreFooter } from '../../components/ui/LoadMoreFooter'
import { useLoadMoreList } from '../../hooks/useLoadMoreList'

const DEFAULT_LIMIT = 20

type OrderRow = {
  id?: unknown
  orderReference?: string
  orderNumber?: string
  createdAt: string
  customerName?: string
  totalAmount?: unknown
  currency?: unknown
  status?: string
  paymentStatus?: string
}

function formatOrderDate(iso: string) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

export default function OrderList() {
  const fetchPage = useCallback(async (page: number, limit: number) => {
    const res = await orderService.fetchOrders({ page, limit })
    return {
      items: (res.items ?? []) as OrderRow[],
      total: res.total,
    }
  }, [])

  const {
    items: orders,
    total: listTotal,
    loading: listLoading,
    loadingMore,
    error: listError,
    hasMore,
    loadMore,
  } = useLoadMoreList<OrderRow>({
    fetchPage,
    limit: DEFAULT_LIMIT,
  })

  return (
    <Card>
      <CardHeader title="Orders" description="Track and manage incoming orders." />
      {listError ? <p className="mb-4 text-sm text-destructive">{listError}</p> : null}
      {listLoading ? (
        <SkeletonListPage columns={7} rows={6} />
      ) : listTotal === 0 ? (
        <EmptyState
          title="No orders yet"
          description="When customers place orders on your storefront, they will appear here for fulfillment."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">S/N</th>
                  <th className="px-4 py-3 font-medium">Order #</th>
                  <th className="px-4 py-3 font-medium">Order date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {orders.map((item, rowIndex) => (
                  <tr key={item.orderReference || String(item.id)}>
                    <td className="px-4 py-3 tabular-nums">{rowIndex + 1}</td>
                    <td className="px-4 py-3 font-mono text-xs sm:text-sm">
                      {String(item.orderReference || item.orderNumber || item.id || '—')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                      {formatOrderDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3">{item.customerName || '-'}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {item.totalAmount != null && item.totalAmount !== ''
                        ? formatMoneyFromUnknown(item.totalAmount, resolveCurrencyCode(item.currency))
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {item.status || '-'}
                      {item.paymentStatus ? (
                        <span className="ml-1 text-xs text-muted-foreground">({item.paymentStatus})</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/orders/${encodeURIComponent(String(item.orderReference || item.id))}`}
                        className="text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <LoadMoreFooter
            shown={orders.length}
            total={listTotal}
            hasMore={hasMore}
            loading={listLoading}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </>
      )}
    </Card>
  )
}
